import React, { useState, useRef } from "react";
import {
  Box,
  Typography,
  Button,
  IconButton,
  TextField,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Divider,
  Slider,
  Switch,
  FormControlLabel,
  CircularProgress,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from "@mui/material";
import { toast } from "react-toastify";

import TitleIcon from "@mui/icons-material/Title";
import NotesIcon from "@mui/icons-material/Notes";
import ImageIcon from "@mui/icons-material/Image";
import VideoLibraryIcon from "@mui/icons-material/VideoLibrary";
import SmartButtonIcon from "@mui/icons-material/SmartButton";
import ShareIcon from "@mui/icons-material/Share";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import VisibilityIcon from "@mui/icons-material/Visibility";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import LinkIcon from "@mui/icons-material/Link";
import FormatBoldIcon from "@mui/icons-material/FormatBold";
import FormatItalicIcon from "@mui/icons-material/FormatItalic";
import FormatAlignLeftIcon from "@mui/icons-material/FormatAlignLeft";
import FormatAlignCenterIcon from "@mui/icons-material/FormatAlignCenter";
import FormatAlignRightIcon from "@mui/icons-material/FormatAlignRight";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import ViewColumnIcon from "@mui/icons-material/ViewColumn";
import SendIcon from "@mui/icons-material/Send";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { usePostEmailTemplateMutation } from "../store/services/emailApi";
import { Form } from "react-router-dom";

// ── Constants ─────────────────────────────────────────────────────────────────
const MAX_IMAGES = 5;
const MAX_VIDEOS = 2;
const UPLOAD_URL = "http://localhost:4000/api/upload-email-image";
const SEND_EMAIL_URL = "http://localhost:4000/api/send-email";

// ── Helper: check if a src is a local/temporary URL (should NOT be saved) ─────
const isLocalSrc = (src) => {
  if (!src) return false;
  if (src.startsWith("blob:")) return true;
  if (src.includes("localhost")) return true;
  if (src.includes("127.0.0.1")) return true;
  return false;
};

const SOCIAL_PLATFORMS = [
  { key: "facebook", label: "Facebook", color: "#1877f2", icon: "f" },
  { key: "instagram", label: "Instagram", color: "#e1306c", icon: "ig" },
  { key: "youtube", label: "YouTube", color: "#ff0000", icon: "yt" },
  { key: "linkedin", label: "LinkedIn", color: "#0a66c2", icon: "in" },
  { key: "whatsapp", label: "WhatsApp", color: "#25d366", icon: "wa" },
  { key: "twitter", label: "Twitter/X", color: "#000000", icon: "x" },
  { key: "tiktok", label: "TikTok", color: "#010101", icon: "tt" },
  { key: "pinterest", label: "Pinterest", color: "#e60023", icon: "p" },
  { key: "telegram", label: "Telegram", color: "#0088cc", icon: "tg" },
  { key: "snapchat", label: "Snapchat", color: "#fffc00", icon: "sc" },
];

const BLOCK_TYPES = [
  { type: "heading", label: "Heading", icon: <TitleIcon />, color: "#1a203e" },
  {
    type: "paragraph",
    label: "Paragraph",
    icon: <NotesIcon />,
    color: "#455a64",
  },
  { type: "image", label: "Image", icon: <ImageIcon />, color: "#1976d2" },
  {
    type: "video",
    label: "Video",
    icon: <VideoLibraryIcon />,
    color: "#c62828",
  },
  {
    type: "button",
    label: "Button",
    icon: <SmartButtonIcon />,
    color: "#2e7d32",
  },
  {
    type: "button_row",
    label: "Button Row",
    icon: <ViewColumnIcon />,
    color: "#00796b",
  },
  {
    type: "social",
    label: "Social Icons",
    icon: <ShareIcon />,
    color: "#7b1fa2",
  },
];

// ── UID factory ───────────────────────────────────────────────────────────────
let idCounter = 1;
const uid = () => `block_${idCounter++}`;

const createButtonItem = () => ({
  id: uid(),
  label: "Click Here",
  url: "https://",
  bgColor: "#1a203e",
  textColor: "#ffffff",
  borderRadius: 4,
});

const createBlock = (type) => {
  const base = { id: uid(), type };
  switch (type) {
    case "heading":
      return {
        ...base,
        text: "Your Heading Here",
        level: "h2",
        align: "left",
        bold: true,
        italic: false,
        color: "#1a203e",
        fontSize: 28,
      };
    case "paragraph":
      return {
        ...base,
        text: "Write your paragraph content here.",
        align: "left",
        bold: false,
        italic: false,
        color: "#444444",
        fontSize: 15,
      };
    case "image":
      return { ...base, src: "", alt: "Image", align: "center", width: 100 };
    case "video":
      return { ...base, src: "", caption: "Video caption", align: "center" };
    case "button":
      return {
        ...base,
        label: "Click Here",
        url: "https://",
        bgColor: "#1a203e",
        textColor: "#ffffff",
        align: "center",
        borderRadius: 4,
        fullWidth: false,
      };
    case "button_row":
      return {
        ...base,
        buttons: [createButtonItem(), createButtonItem()],
        align: "center",
        gap: 12,
      };
    case "social":
      return { ...base, platforms: [] };
    default:
      return base;
  }
};

// ── Social badge ──────────────────────────────────────────────────────────────
const SocialBadge = ({ platform }) => (
  <Box
    sx={{
      width: 36,
      height: 36,
      borderRadius: "50%",
      backgroundColor: platform.color,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexShrink: 0,
    }}
  >
    <Typography
      sx={{
        fontSize: "0.6rem",
        fontWeight: 800,
        color: platform.key === "snapchat" ? "#000" : "#fff",
        textTransform: "uppercase",
      }}
    >
      {platform.icon}
    </Typography>
  </Box>
);

// ── Block renderer ────────────────────────────────────────────────────────────
const BlockRenderer = ({ block }) => {
  switch (block.type) {
    case "heading":
      return (
        <Typography
          sx={{
            fontWeight: block.bold ? 700 : 400,
            fontStyle: block.italic ? "italic" : "normal",
            textAlign: block.align,
            color: block.color,
            fontSize: `${block.fontSize}px`,
            lineHeight: 1.3,
            wordBreak: "break-word",
          }}
        >
          {block.text || "Heading"}
        </Typography>
      );
    case "paragraph":
      return (
        <Typography
          sx={{
            fontWeight: block.bold ? 600 : 400,
            fontStyle: block.italic ? "italic" : "normal",
            textAlign: block.align,
            color: block.color,
            fontSize: `${block.fontSize}px`,
            lineHeight: 1.7,
            wordBreak: "break-word",
          }}
        >
          {block.text || "Paragraph text"}
        </Typography>
      );
    case "image":
      return (
        <Box sx={{ textAlign: block.align }}>
          {block.src ? (
            <img
              src={block.src}
              alt={block.alt}
              style={{
                width: `${block.width}%`,
                maxWidth: "100%",
                display: "inline-block",
                borderRadius: 4,
              }}
            />
          ) : (
            <Box
              sx={{
                display: "inline-flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                width: `${block.width}%`,
                minHeight: 120,
                backgroundColor: "#f0f4f8",
                border: "2px dashed #b0bec5",
                borderRadius: "6px",
                gap: 0.5,
              }}
            >
              <ImageIcon sx={{ fontSize: 32, color: "#b0bec5" }} />
              <Typography sx={{ fontSize: "0.72rem", color: "#90a4ae" }}>
                No image selected
              </Typography>
            </Box>
          )}
        </Box>
      );
    case "video":
      return (
        <Box sx={{ textAlign: block.align }}>
          {block.src ? (
            block.src.startsWith("blob:") ? (
              <video controls style={{ width: "100%", borderRadius: 6 }}>
                <source src={block.src} />
              </video>
            ) : (
              <Box
                sx={{
                  position: "relative",
                  paddingBottom: "56.25%",
                  height: 0,
                  overflow: "hidden",
                  borderRadius: "6px",
                }}
              >
                <iframe
                  src={block.src}
                  title="video"
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                    border: "none",
                    borderRadius: 6,
                  }}
                  allowFullScreen
                />
              </Box>
            )
          ) : (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                minHeight: 120,
                backgroundColor: "#1a1a2e",
                border: "2px dashed #37474f",
                borderRadius: "6px",
                gap: 0.5,
              }}
            >
              <VideoLibraryIcon sx={{ fontSize: 32, color: "#546e7a" }} />
              <Typography sx={{ fontSize: "0.72rem", color: "#546e7a" }}>
                No video URL set
              </Typography>
            </Box>
          )}
          {block.caption && (
            <Typography
              sx={{
                fontSize: "0.72rem",
                color: "#90a4ae",
                textAlign: "center",
                mt: 0.5,
              }}
            >
              {block.caption}
            </Typography>
          )}
        </Box>
      );
    case "button":
      return (
        <Box sx={{ textAlign: block.align }}>
          <Box
            component="a"
            href={block.url || "#"}
            target="_blank"
            rel="noreferrer"
            sx={{
              display: "inline-block",
              px: 3,
              py: 1,
              backgroundColor: block.bgColor,
              color: block.textColor,
              borderRadius: `${block.borderRadius}px`,
              fontSize: "0.85rem",
              fontWeight: 600,
              textDecoration: "none",
              cursor: "pointer",
              width: block.fullWidth ? "100%" : "auto",
              textAlign: "center",
              boxSizing: "border-box",
              transition: "opacity 0.2s",
              "&:hover": { opacity: 0.85 },
            }}
          >
            {block.label || "Button"}
          </Box>
        </Box>
      );
    case "button_row":
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: block.align || "center",
            flexWrap: "wrap",
            gap: `${block.gap || 12}px`,
          }}
        >
          {block.buttons && block.buttons.length > 0 ? (
            block.buttons.map((btn) => (
              <Box
                key={btn.id}
                component="a"
                href={btn.url || "#"}
                target="_blank"
                rel="noreferrer"
                sx={{
                  display: "inline-block",
                  px: 3,
                  py: 1,
                  backgroundColor: btn.bgColor,
                  color: btn.textColor,
                  borderRadius: `${btn.borderRadius}px`,
                  fontSize: "0.85rem",
                  fontWeight: 600,
                  textDecoration: "none",
                  cursor: "pointer",
                  textAlign: "center",
                  transition: "opacity 0.2s",
                  "&:hover": { opacity: 0.85 },
                }}
              >
                {btn.label || "Button"}
              </Box>
            ))
          ) : (
            <Typography
              sx={{
                fontSize: "0.72rem",
                color: "#90a4ae",
                fontStyle: "italic",
              }}
            >
              No buttons added
            </Typography>
          )}
        </Box>
      );
    case "social":
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            gap: 1,
            flexWrap: "wrap",
          }}
        >
          {block.platforms.length === 0 ? (
            <Typography
              sx={{
                fontSize: "0.72rem",
                color: "#90a4ae",
                fontStyle: "italic",
              }}
            >
              No social icons added yet
            </Typography>
          ) : (
            block.platforms.map((p) => {
              const meta = SOCIAL_PLATFORMS.find((s) => s.key === p.key);
              return meta ? (
                <Tooltip title={p.url || meta.label} key={p.key}>
                  <Box
                    component="a"
                    href={p.url || "#"}
                    target="_blank"
                    rel="noreferrer"
                    sx={{ textDecoration: "none" }}
                  >
                    <SocialBadge platform={meta} />
                  </Box>
                </Tooltip>
              ) : null;
            })
          )}
        </Box>
      );
    default:
      return null;
  }
};

// ── Properties panel ──────────────────────────────────────────────────────────
const PropertiesPanel = ({ block, onChange, imageCount, videoCount }) => {
  const imageFileRef = useRef(null);
  const videoFileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState("");

  if (!block)
    return (
      <Box sx={{ p: 3, textAlign: "center" }}>
        <TitleIcon sx={{ fontSize: 40, color: "#e0e0e0", mb: 1 }} />
        <Typography sx={{ fontSize: "0.78rem", color: "#bbb" }}>
          Select a block to edit its properties
        </Typography>
      </Box>
    );

  const update = (key, val) => onChange({ ...block, [key]: val });

  const AlignBtn = ({ val }) => (
    <IconButton
      size="small"
      onClick={() => update("align", val)}
      sx={{
        border: `1px solid ${block.align === val ? "#1a203e" : "#e0e0e0"}`,
        borderRadius: "4px",
        backgroundColor: block.align === val ? "#1a203e" : "transparent",
        color: block.align === val ? "#fff" : "#555",
        p: 0.5,
      }}
    >
      {val === "left" ? (
        <FormatAlignLeftIcon sx={{ fontSize: 16 }} />
      ) : val === "center" ? (
        <FormatAlignCenterIcon sx={{ fontSize: 16 }} />
      ) : (
        <FormatAlignRightIcon sx={{ fontSize: 16 }} />
      )}
    </IconButton>
  );

  const label = (t) => (
    <Typography sx={{ fontSize: "0.72rem", color: "#777", mb: 0.5, mt: 1.5 }}>
      {t}
    </Typography>
  );

  const UploadStatus = ({ src }) => (
    <>
      {uploadError && (
        <Typography
          sx={{
            fontSize: "0.7rem",
            color: "#c62828",
            mt: 0.5,
            mb: 0.5,
            backgroundColor: "#fce4ec",
            p: 0.75,
            borderRadius: "4px",
          }}
        >
          ❌ {uploadError}
        </Typography>
      )}
      {src && src.startsWith("blob:") && (
        <Typography
          sx={{
            fontSize: "0.68rem",
            color: "#f57c00",
            mt: 0.5,
            mb: 0.5,
            backgroundColor: "#fff3e0",
            p: 0.5,
            borderRadius: "4px",
          }}
        >
          ✅ Local preview — will be converted to base64 on save
        </Typography>
      )}
      {src && isLocalSrc(src) && !src.startsWith("blob:") && (
        <Typography
          sx={{
            fontSize: "0.68rem",
            color: "#e65100",
            mt: 0.5,
            mb: 0.5,
            backgroundColor: "#fff3e0",
            p: 0.5,
            borderRadius: "4px",
          }}
        >
          ⚠️ Local URL — will be excluded from saved template
        </Typography>
      )}
      {src && !isLocalSrc(src) && src.startsWith("http") && (
        <Typography
          sx={{
            fontSize: "0.68rem",
            color: "#2e7d32",
            mt: 0.5,
            mb: 0.5,
            backgroundColor: "#e8f5e9",
            p: 0.5,
            borderRadius: "4px",
          }}
        >
          ✅ Public URL — will be included in saved template
        </Typography>
      )}
      {src && src.startsWith("data:") && (
        <Typography
          sx={{
            fontSize: "0.68rem",
            color: "#2e7d32",
            mt: 0.5,
            mb: 0.5,
            backgroundColor: "#e8f5e9",
            p: 0.5,
            borderRadius: "4px",
          }}
        >
          ✅ Base64 Media — will be included in saved template
        </Typography>
      )}
    </>
  );

  const handleImageFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadError("");
    const localPreview = URL.createObjectURL(file);
    update("src", localPreview);
    e.target.value = "";
  };

  const handleVideoFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadError("");
    const localPreview = URL.createObjectURL(file);
    update("src", localPreview);
    e.target.value = "";
  };

  if (block.type === "heading" || block.type === "paragraph") {
    return (
      <Box sx={{ p: 1.5 }}>
        <Typography
          sx={{
            fontSize: "0.78rem",
            fontWeight: 700,
            color: "#1a203e",
            mb: 1.5,
            textTransform: "uppercase",
            letterSpacing: 0.5,
          }}
        >
          {block.type === "heading" ? "Heading" : "Paragraph"} Settings
        </Typography>
        {label("Content")}
        <TextField
          multiline
          minRows={3}
          fullWidth
          size="small"
          value={block.text}
          onChange={(e) => update("text", e.target.value)}
          sx={{ "& textarea": { fontSize: "0.78rem" } }}
        />
        {label("Font Size")}
        <Box sx={{ px: 0.5 }}>
          <Slider
            min={10}
            max={block.type === "heading" ? 60 : 24}
            value={block.fontSize}
            onChange={(_, v) => update("fontSize", v)}
            size="small"
            valueLabelDisplay="auto"
            marks={[
              { value: block.type === "heading" ? 28 : 14, label: "Default" },
            ]}
          />
        </Box>
        {label("Alignment")}
        <Box sx={{ display: "flex", gap: 0.5 }}>
          <AlignBtn val="left" />
          <AlignBtn val="center" />
          <AlignBtn val="right" />
        </Box>
        {label("Style")}
        <Box sx={{ display: "flex", gap: 0.75 }}>
          <IconButton
            size="small"
            onClick={() => update("bold", !block.bold)}
            sx={{
              border: `1px solid ${block.bold ? "#1a203e" : "#e0e0e0"}`,
              borderRadius: "4px",
              backgroundColor: block.bold ? "#1a203e" : "transparent",
              color: block.bold ? "#fff" : "#555",
              p: 0.5,
            }}
          >
            <FormatBoldIcon sx={{ fontSize: 16 }} />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => update("italic", !block.italic)}
            sx={{
              border: `1px solid ${block.italic ? "#1a203e" : "#e0e0e0"}`,
              borderRadius: "4px",
              backgroundColor: block.italic ? "#1a203e" : "transparent",
              color: block.italic ? "#fff" : "#555",
              p: 0.5,
            }}
          >
            <FormatItalicIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Box>
        {label("Text Color")}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <input
            type="color"
            value={block.color}
            onChange={(e) => update("color", e.target.value)}
            style={{
              width: 36,
              height: 28,
              border: "1px solid #e0e0e0",
              borderRadius: 4,
              cursor: "pointer",
              padding: 2,
            }}
          />
          <Typography sx={{ fontSize: "0.72rem", color: "#888" }}>
            {block.color}
          </Typography>
        </Box>
      </Box>
    );
  }

  if (block.type === "image") {
    const atLimit = !block.src && imageCount >= MAX_IMAGES;
    return (
      <Box sx={{ p: 1.5 }}>
        <Typography
          sx={{
            fontSize: "0.78rem",
            fontWeight: 700,
            color: "#1a203e",
            mb: 1.5,
            textTransform: "uppercase",
            letterSpacing: 0.5,
          }}
        >
          Image Settings
        </Typography>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 1,
          }}
        >
          <Typography sx={{ fontSize: "0.72rem", color: "#777" }}>
            Usage
          </Typography>
          <Chip
            label={`${imageCount} / ${MAX_IMAGES}`}
            size="small"
            sx={{
              fontSize: "0.65rem",
              height: 18,
              backgroundColor: imageCount >= MAX_IMAGES ? "#fce4ec" : "#e8f5e9",
              color: imageCount >= MAX_IMAGES ? "#c62828" : "#2e7d32",
            }}
          />
        </Box>
        {atLimit && (
          <Typography
            sx={{
              fontSize: "0.7rem",
              color: "#c62828",
              mb: 1,
              backgroundColor: "#fce4ec",
              p: 0.75,
              borderRadius: "4px",
            }}
          >
            Max {MAX_IMAGES} images reached
          </Typography>
        )}
        {label("Upload from Device")}
        <input
          ref={imageFileRef}
          type="file"
          accept="image/*"
          style={{ display: "none" }}
          onChange={handleImageFileChange}
        />
        <Button
          size="small"
          variant="outlined"
          startIcon={<UploadFileIcon sx={{ fontSize: 15 }} />}
          disabled={atLimit || uploading}
          onClick={() => imageFileRef.current?.click()}
          fullWidth
          sx={{
            fontSize: "0.72rem",
            textTransform: "none",
            mb: 0.5,
            borderColor: "#c5cae9",
            color: "#3949ab",
            "&:hover": { borderColor: "#3949ab", backgroundColor: "#f0f4ff" },
          }}
        >
          {uploading ? "Uploading..." : "Choose Image File"}
        </Button>
        <UploadStatus src={block.src} />
        {label("Or paste Image URL")}
        <TextField
          fullWidth
          size="small"
          placeholder="https://your-image.com/photo.jpg"
          value={
            block.src && block.src.startsWith("blob:")
              ? "(uploading preview...)"
              : block.src
          }
          onChange={(e) => {
            setUploadError("");
            update("src", e.target.value);
          }}
          disabled={atLimit}
          sx={{ "& input": { fontSize: "0.78rem" } }}
        />
        {block.src && (
          <Button
            size="small"
            onClick={() => {
              update("src", "");
              setUploadError("");
            }}
            sx={{
              mt: 0.5,
              fontSize: "0.68rem",
              color: "#e57373",
              textTransform: "none",
              p: 0,
            }}
          >
            Clear image
          </Button>
        )}
        {label("Alt Text")}
        <TextField
          fullWidth
          size="small"
          value={block.alt}
          onChange={(e) => update("alt", e.target.value)}
          sx={{ "& input": { fontSize: "0.78rem" } }}
        />
        {label("Width")}
        <Box sx={{ px: 0.5 }}>
          <Slider
            min={20}
            max={100}
            value={block.width}
            onChange={(_, v) => update("width", v)}
            size="small"
            valueLabelDisplay="auto"
            valueLabelFormat={(v) => `${v}%`}
          />
        </Box>
        {label("Alignment")}
        <Box sx={{ display: "flex", gap: 0.5 }}>
          <AlignBtn val="left" />
          <AlignBtn val="center" />
          <AlignBtn val="right" />
        </Box>
      </Box>
    );
  }

  if (block.type === "video") {
    const atLimit = !block.src && videoCount >= MAX_VIDEOS;
    return (
      <Box sx={{ p: 1.5 }}>
        <Typography
          sx={{
            fontSize: "0.78rem",
            fontWeight: 700,
            color: "#1a203e",
            mb: 1.5,
            textTransform: "uppercase",
            letterSpacing: 0.5,
          }}
        >
          Video Settings
        </Typography>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 1,
          }}
        >
          <Typography sx={{ fontSize: "0.72rem", color: "#777" }}>
            Usage
          </Typography>
          <Chip
            label={`${videoCount} / ${MAX_VIDEOS}`}
            size="small"
            sx={{
              fontSize: "0.65rem",
              height: 18,
              backgroundColor: videoCount >= MAX_VIDEOS ? "#fce4ec" : "#e8f5e9",
              color: videoCount >= MAX_VIDEOS ? "#c62828" : "#2e7d32",
            }}
          />
        </Box>
        {atLimit && (
          <Typography
            sx={{
              fontSize: "0.7rem",
              color: "#c62828",
              mb: 1,
              backgroundColor: "#fce4ec",
              p: 0.75,
              borderRadius: "4px",
            }}
          >
            Max {MAX_VIDEOS} videos reached
          </Typography>
        )}
        {label("Upload from Device")}
        <input
          ref={videoFileRef}
          type="file"
          accept="video/*"
          style={{ display: "none" }}
          onChange={handleVideoFileChange}
        />
        <Button
          size="small"
          variant="outlined"
          startIcon={<UploadFileIcon sx={{ fontSize: 15 }} />}
          disabled={atLimit || uploading}
          onClick={() => videoFileRef.current?.click()}
          fullWidth
          sx={{
            fontSize: "0.72rem",
            textTransform: "none",
            mb: 0.5,
            borderColor: "#ffcdd2",
            color: "#c62828",
            "&:hover": { borderColor: "#c62828", backgroundColor: "#fff5f5" },
          }}
        >
          {uploading ? "Uploading..." : "Choose Video File"}
        </Button>
        <UploadStatus src={block.src} />
        {label("Or paste Embed URL")}
        <TextField
          fullWidth
          size="small"
          placeholder="https://www.youtube.com/embed/..."
          value={
            block.src && block.src.startsWith("blob:")
              ? "(uploading preview...)"
              : block.src
          }
          onChange={(e) => {
            setUploadError("");
            update("src", e.target.value);
          }}
          disabled={atLimit}
          sx={{ "& input": { fontSize: "0.78rem" } }}
          helperText={
            <span style={{ fontSize: "0.65rem" }}>
              Use YouTube embed URL (youtube.com/embed/ID)
            </span>
          }
        />
        {block.src && (
          <Button
            size="small"
            onClick={() => {
              update("src", "");
              setUploadError("");
            }}
            sx={{
              mt: 0.5,
              fontSize: "0.68rem",
              color: "#e57373",
              textTransform: "none",
              p: 0,
            }}
          >
            Clear video
          </Button>
        )}
        {label("Caption")}
        <TextField
          fullWidth
          size="small"
          value={block.caption}
          onChange={(e) => update("caption", e.target.value)}
          sx={{ "& input": { fontSize: "0.78rem" } }}
        />
      </Box>
    );
  }

  if (block.type === "button") {
    return (
      <Box sx={{ p: 1.5 }}>
        <Typography
          sx={{
            fontSize: "0.78rem",
            fontWeight: 700,
            color: "#1a203e",
            mb: 1.5,
            textTransform: "uppercase",
            letterSpacing: 0.5,
          }}
        >
          Button Settings
        </Typography>
        {label("Button Label")}
        <TextField
          fullWidth
          size="small"
          value={block.label}
          onChange={(e) => update("label", e.target.value)}
          sx={{ "& input": { fontSize: "0.78rem" } }}
        />
        {label("URL / Link")}
        <TextField
          fullWidth
          size="small"
          placeholder="https://yourwebsite.com"
          value={block.url}
          onChange={(e) => update("url", e.target.value)}
          InputProps={{
            startAdornment: (
              <LinkIcon sx={{ fontSize: 15, color: "#aaa", mr: 0.5 }} />
            ),
          }}
          sx={{ "& input": { fontSize: "0.78rem" } }}
        />
        {label("Background Color")}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <input
            type="color"
            value={block.bgColor}
            onChange={(e) => update("bgColor", e.target.value)}
            style={{
              width: 36,
              height: 28,
              border: "1px solid #e0e0e0",
              borderRadius: 4,
              cursor: "pointer",
              padding: 2,
            }}
          />
          <Typography sx={{ fontSize: "0.72rem", color: "#888" }}>
            {block.bgColor}
          </Typography>
        </Box>
        {label("Text Color")}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <input
            type="color"
            value={block.textColor}
            onChange={(e) => update("textColor", e.target.value)}
            style={{
              width: 36,
              height: 28,
              border: "1px solid #e0e0e0",
              borderRadius: 4,
              cursor: "pointer",
              padding: 2,
            }}
          />
          <Typography sx={{ fontSize: "0.72rem", color: "#888" }}>
            {block.textColor}
          </Typography>
        </Box>
        {label("Border Radius")}
        <Box sx={{ px: 0.5 }}>
          <Slider
            min={0}
            max={30}
            value={block.borderRadius}
            onChange={(_, v) => update("borderRadius", v)}
            size="small"
            valueLabelDisplay="auto"
            valueLabelFormat={(v) => `${v}px`}
          />
        </Box>
        {label("Alignment")}
        <Box sx={{ display: "flex", gap: 0.5 }}>
          <AlignBtn val="left" />
          <AlignBtn val="center" />
          <AlignBtn val="right" />
        </Box>
        {label("Full Width")}
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={block.fullWidth}
              onChange={(e) => update("fullWidth", e.target.checked)}
            />
          }
          label={
            <Typography sx={{ fontSize: "0.75rem" }}>
              Stretch to full width
            </Typography>
          }
          sx={{ m: 0 }}
        />
      </Box>
    );
  }

  if (block.type === "button_row") {
    const updateBtn = (id, key, val) =>
      update(
        "buttons",
        block.buttons.map((b) => (b.id === id ? { ...b, [key]: val } : b)),
      );
    const removeBtn = (id) =>
      update(
        "buttons",
        block.buttons.filter((b) => b.id !== id),
      );
    const addBtn = () =>
      update("buttons", [...block.buttons, createButtonItem()]);

    const RowAlignBtn = ({ val }) => (
      <IconButton
        size="small"
        onClick={() => update("align", val)}
        sx={{
          border: `1px solid ${block.align === val ? "#1a203e" : "#e0e0e0"}`,
          borderRadius: "4px",
          backgroundColor: block.align === val ? "#1a203e" : "transparent",
          color: block.align === val ? "#fff" : "#555",
          p: 0.5,
        }}
      >
        {val === "flex-start" ? (
          <FormatAlignLeftIcon sx={{ fontSize: 16 }} />
        ) : val === "center" ? (
          <FormatAlignCenterIcon sx={{ fontSize: 16 }} />
        ) : (
          <FormatAlignRightIcon sx={{ fontSize: 16 }} />
        )}
      </IconButton>
    );

    return (
      <Box sx={{ p: 1.5 }}>
        <Typography
          sx={{
            fontSize: "0.78rem",
            fontWeight: 700,
            color: "#1a203e",
            mb: 1.5,
            textTransform: "uppercase",
            letterSpacing: 0.5,
          }}
        >
          Button Row Settings
        </Typography>
        {label("Alignment")}
        <Box sx={{ display: "flex", gap: 0.5, mb: 0.5 }}>
          <RowAlignBtn val="flex-start" />
          <RowAlignBtn val="center" />
          <RowAlignBtn val="flex-end" />
        </Box>
        {label("Gap Between Buttons")}
        <Box sx={{ px: 0.5 }}>
          <Slider
            min={4}
            max={40}
            value={block.gap || 12}
            onChange={(_, v) => update("gap", v)}
            size="small"
            valueLabelDisplay="auto"
            valueLabelFormat={(v) => `${v}px`}
          />
        </Box>
        <Divider sx={{ my: 1.5 }} />
        {block.buttons.map((btn, idx) => (
          <Box
            key={btn.id}
            sx={{
              mb: 1.5,
              p: 1.25,
              border: "1px solid #f0f0f0",
              borderRadius: "8px",
              backgroundColor: "#fafafa",
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 0.75,
              }}
            >
              <Typography
                sx={{ fontSize: "0.72rem", fontWeight: 700, color: "#555" }}
              >
                Button {idx + 1}
              </Typography>
              {block.buttons.length > 1 && (
                <IconButton
                  size="small"
                  onClick={() => removeBtn(btn.id)}
                  sx={{ p: 0.25 }}
                >
                  <CloseIcon sx={{ fontSize: 13, color: "#e57373" }} />
                </IconButton>
              )}
            </Box>
            <Typography sx={{ fontSize: "0.68rem", color: "#999", mb: 0.4 }}>
              Label
            </Typography>
            <TextField
              fullWidth
              size="small"
              value={btn.label}
              onChange={(e) => updateBtn(btn.id, "label", e.target.value)}
              sx={{ mb: 0.75, "& input": { fontSize: "0.75rem" } }}
            />
            <Typography sx={{ fontSize: "0.68rem", color: "#999", mb: 0.4 }}>
              URL
            </Typography>
            <TextField
              fullWidth
              size="small"
              value={btn.url}
              onChange={(e) => updateBtn(btn.id, "url", e.target.value)}
              sx={{ mb: 0.75, "& input": { fontSize: "0.75rem" } }}
            />
            <Box sx={{ display: "flex", gap: 1.5, alignItems: "center" }}>
              <Box sx={{ flex: 1 }}>
                <Typography
                  sx={{ fontSize: "0.68rem", color: "#999", mb: 0.4 }}
                >
                  BG
                </Typography>
                <input
                  type="color"
                  value={btn.bgColor}
                  onChange={(e) => updateBtn(btn.id, "bgColor", e.target.value)}
                  style={{
                    width: "100%",
                    height: 26,
                    border: "1px solid #e0e0e0",
                    borderRadius: 4,
                    cursor: "pointer",
                    padding: 2,
                  }}
                />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography
                  sx={{ fontSize: "0.68rem", color: "#999", mb: 0.4 }}
                >
                  Text
                </Typography>
                <input
                  type="color"
                  value={btn.textColor}
                  onChange={(e) =>
                    updateBtn(btn.id, "textColor", e.target.value)
                  }
                  style={{
                    width: "100%",
                    height: 26,
                    border: "1px solid #e0e0e0",
                    borderRadius: 4,
                    cursor: "pointer",
                    padding: 2,
                  }}
                />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography
                  sx={{ fontSize: "0.68rem", color: "#999", mb: 0.4 }}
                >
                  Radius
                </Typography>
                <TextField
                  size="small"
                  type="number"
                  value={btn.borderRadius}
                  onChange={(e) =>
                    updateBtn(btn.id, "borderRadius", Number(e.target.value))
                  }
                  inputProps={{
                    min: 0,
                    max: 30,
                    style: { fontSize: "0.72rem", padding: "3px 6px" },
                  }}
                />
              </Box>
            </Box>
          </Box>
        ))}
        <Button
          size="small"
          startIcon={<AddIcon sx={{ fontSize: 14 }} />}
          onClick={addBtn}
          fullWidth
          variant="outlined"
          sx={{
            fontSize: "0.72rem",
            textTransform: "none",
            borderColor: "#c5cae9",
            color: "#3949ab",
            "&:hover": { borderColor: "#3949ab", backgroundColor: "#f0f4ff" },
          }}
        >
          Add Another Button
        </Button>
      </Box>
    );
  }

  if (block.type === "social") {
    const addPlatform = (key) => {
      if (block.platforms.find((p) => p.key === key)) return;
      update("platforms", [...block.platforms, { key, url: "" }]);
    };
    const removePlatform = (key) =>
      update(
        "platforms",
        block.platforms.filter((p) => p.key !== key),
      );
    const updateUrl = (key, url) =>
      update(
        "platforms",
        block.platforms.map((p) => (p.key === key ? { ...p, url } : p)),
      );
    const available = SOCIAL_PLATFORMS.filter(
      (p) => !block.platforms.find((b) => b.key === p.key),
    );

    return (
      <Box sx={{ p: 1.5 }}>
        <Typography
          sx={{
            fontSize: "0.78rem",
            fontWeight: 700,
            color: "#1a203e",
            mb: 1.5,
            textTransform: "uppercase",
            letterSpacing: 0.5,
          }}
        >
          Social Icons Settings
        </Typography>
        {available.length > 0 && (
          <>
            <Typography sx={{ fontSize: "0.72rem", color: "#777", mb: 0.75 }}>
              Add Platform
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mb: 1.5 }}>
              {available.map((p) => (
                <Chip
                  key={p.key}
                  label={p.label}
                  size="small"
                  onClick={() => addPlatform(p.key)}
                  icon={<AddIcon sx={{ fontSize: 12 }} />}
                  sx={{
                    fontSize: "0.65rem",
                    height: 22,
                    cursor: "pointer",
                    backgroundColor: "#f5f5f5",
                    "&:hover": { backgroundColor: "#e3f2fd" },
                  }}
                />
              ))}
            </Box>
          </>
        )}
        {block.platforms.length === 0 ? (
          <Typography
            sx={{ fontSize: "0.72rem", color: "#bbb", fontStyle: "italic" }}
          >
            No platforms added yet
          </Typography>
        ) : (
          block.platforms.map((p) => {
            const meta = SOCIAL_PLATFORMS.find((s) => s.key === p.key);
            return (
              <Box
                key={p.key}
                sx={{
                  mb: 1,
                  p: 1,
                  border: "1px solid #f0f0f0",
                  borderRadius: "6px",
                  backgroundColor: "#fafafa",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    mb: 0.5,
                  }}
                >
                  <Box
                    sx={{ display: "flex", alignItems: "center", gap: 0.75 }}
                  >
                    <SocialBadge platform={meta} />
                    <Typography sx={{ fontSize: "0.75rem", fontWeight: 600 }}>
                      {meta?.label}
                    </Typography>
                  </Box>
                  <IconButton
                    size="small"
                    onClick={() => removePlatform(p.key)}
                    sx={{ p: 0.25 }}
                  >
                    <CloseIcon sx={{ fontSize: 14, color: "#e57373" }} />
                  </IconButton>
                </Box>
                <TextField
                  fullWidth
                  size="small"
                  placeholder={`https://${meta?.key}.com/yourpage`}
                  value={p.url}
                  onChange={(e) => updateUrl(p.key, e.target.value)}
                  sx={{ "& input": { fontSize: "0.72rem" } }}
                />
              </Box>
            );
          })
        )}
      </Box>
    );
  }

  return null;
};

// ── HTML Generator for Email ────────────────────────────────────────────────
// NOTE: Uses sanitized blocks — local/blob URLs are already stripped before this is called
const generateHTML = (blocks, bgColor, emailBg) => {
  const blockToHtml = (block) => {
    switch (block.type) {
      case "heading":
        return `<h1 style="margin: 0; font-family: sans-serif; font-size: ${block.fontSize}px; font-weight: ${block.bold ? "bold" : "normal"}; font-style: ${block.italic ? "italic" : "normal"}; color: ${block.color}; text-align: ${block.align}; line-height: 1.3;">${block.text || ""}</h1>`;
      case "paragraph":
        return `<p style="margin: 0; font-family: sans-serif; font-size: ${block.fontSize}px; font-weight: ${block.bold ? "bold" : "normal"}; font-style: ${block.italic ? "italic" : "normal"}; color: ${block.color}; text-align: ${block.align}; line-height: 1.7;">${block.text || ""}</p>`;
      case "image":
        // ── FIXED: skip any image with no src or a local/blob src ──
        if (!block.src || isLocalSrc(block.src)) return "";
        return `<div style="text-align: ${block.align};"><img src="${block.src}" alt="${block.alt || ""}" style="width: ${block.width}%; max-width: 100%; height: auto; display: inline-block; border-radius: 4px;" /></div>`;
      case "video":
        // ── FIXED: skip any video with no src or a local/blob src ──
        if (!block.src || isLocalSrc(block.src)) return "";
        return `<div style="text-align: ${block.align};">
                    <a href="${block.src}" target="_blank" style="text-decoration: none; display: inline-block; width: 100%;">
                        <div style="background-color: #000; color: #fff; padding: 40px; text-align: center; border-radius: 6px;">
                            <span style="font-size: 40px;">▶</span><br/>
                            <span style="font-size: 14px; font-family: sans-serif;">Click to view video</span>
                        </div>
                    </a>
                    ${block.caption ? `<p style="font-size: 12px; color: #90a4ae; text-align: center; margin-top: 5px; font-family: sans-serif;">${block.caption}</p>` : ""}
                </div>`;
      case "button":
        return `<div style="text-align: ${block.align};">
                    <a href="${block.url || "#"}" target="_blank" style="display: ${block.fullWidth ? "block" : "inline-block"}; background-color: ${block.bgColor}; color: ${block.textColor}; padding: 12px 24px; text-decoration: none; border-radius: ${block.borderRadius}px; font-family: sans-serif; font-size: 14px; font-weight: bold; width: ${block.fullWidth ? "100%" : "auto"}; box-sizing: border-box;">
                        ${block.label || "Button"}
                    </a>
                </div>`;
      case "button_row":
        return `<div style="text-align: ${block.align === "flex-start" ? "left" : block.align === "flex-end" ? "right" : "center"};">
                    <table border="0" cellpadding="0" cellspacing="0" style="display: inline-table;">
                        <tr>
                            ${(block.buttons || [])
                              .map(
                                (btn, i) => `
                                <td align="center" style="padding: 0 ${i === block.buttons.length - 1 ? 0 : block.gap || 12}px 10px 0;">
                                    <a href="${btn.url || "#"}" target="_blank" style="display: inline-block; background-color: ${btn.bgColor}; color: ${btn.textColor}; padding: 10px 20px; text-decoration: none; border-radius: ${btn.borderRadius}px; font-family: sans-serif; font-size: 14px; font-weight: bold;">
                                        ${btn.label || "Button"}
                                    </a>
                                </td>
                            `,
                              )
                              .join("")}
                        </tr>
                    </table>
                </div>`;
      case "social":
        return `<div style="text-align: center;">
                    <table border="0" cellpadding="0" cellspacing="0" style="display: inline-table;">
                        <tr>
                            ${block.platforms
                              .map((p) => {
                                const meta = SOCIAL_PLATFORMS.find(
                                  (s) => s.key === p.key,
                                );
                                if (!meta) return "";
                                return `
                                    <td style="padding: 0 4px;">
                                        <a href="${p.url || "#"}" target="_blank" style="display: inline-block; width: 32px; height: 32px; background-color: ${meta.color}; border-radius: 50%; color: ${meta.key === "snapchat" ? "#000" : "#fff"}; text-align: center; line-height: 32px; text-decoration: none; font-family: sans-serif; font-size: 12px; font-weight: bold;">
                                            ${meta.icon.toUpperCase()}
                                        </a>
                                    </td>
                                `;
                              })
                              .join("")}
                        </tr>
                    </table>
                </div>`;
      default:
        return "";
    }
  };

  return `<!DOCTYPE html>
<html>
<head>
    <meta charset="utf-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Email Template</title>
</head>
<body style="margin: 0; padding: 0; background-color: ${bgColor};">
    ${blocks
      .map((b) => {
        const html = blockToHtml(b);
        return html ? `<div style="margin-bottom: 16px;">${html}</div>` : "";
      })
      .filter(Boolean)
      .join("\n    ")}
</body>
</html>`.trim();
};

// ── Sanitize blocks before saving: strip all local/blob src values ─────────────
const sanitizeBlocksForSave = (blocks) =>
  blocks.map((block) => {
    if (
      (block.type === "image" || block.type === "video") &&
      isLocalSrc(block.src)
    ) {
      return { ...block, src: "" };
    }
    return block;
  });

const blobToBase64 = async (blobUrl) => {
  try {
    const response = await fetch(blobUrl);
    const blob = await response.blob();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (e) {
    return "";
  }
};

// ── Main Component ─────────────────────────────────────────────────────────────
const EmailTemplateBuilder = () => {
  const [templateName, setTemplateName] = useState("Untitled Template");
  const [blocks, setBlocks] = useState([
    createBlock("heading"),
    createBlock("paragraph"),
  ]);
  const [selectedId, setSelectedId] = useState(null);
  const [dragOverId, setDragOverId] = useState(null);
  const [dragId, setDragId] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [savedOk, setSavedOk] = useState(false);
  const [bgColor, setBgColor] = useState("#ffffff1e");
  const [emailBg, setEmailBg] = useState("#f4f4f4");
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [saveData, setSaveData] = useState(null);
  const [template_Category, setTemplateCategory] = useState("");

  // ── Email send state ───────────────────────────────────────────────────────
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [emailError, setEmailError] = useState("");
  const [isSaving, setIsSaving] = useState(false);


  const [postEmailTemplate] = usePostEmailTemplateMutation();

  const handlesubmit = () => {
    if (!saveData?.html) return;
    setIsSaving(true);
    postEmailTemplate({
      email_template_name: templateName,
      category: template_Category,
      email_template_data: saveData.html,
    })
      .unwrap()
      .then((res) => {
        console.log(res);
        toast.success(res?.message);
        setSaveDialogOpen(false);
      })
      .catch((err) => {
        console.log(err);
        toast.error(err?.data?.message);
      })
      .finally(() => {
        setIsSaving(false);
      });
  };

  const dragItem = useRef(null);

  // ── Count only blocks that have a real public src (not local) ─────────────
  const imageCount = blocks.filter(
    (b) => b.type === "image" && b.src && !isLocalSrc(b.src),
  ).length;
  const videoCount = blocks.filter(
    (b) => b.type === "video" && b.src && !isLocalSrc(b.src),
  ).length;
  const totalImages = blocks.filter((b) => b.type === "image").length;
  const totalVideos = blocks.filter((b) => b.type === "video").length;
  const canAddImage = totalImages < MAX_IMAGES;
  const canAddVideo = totalVideos < MAX_VIDEOS;

  const addBlock = (type) => {
    if (type === "image" && !canAddImage) return;
    if (type === "video" && !canAddVideo) return;
    const block = createBlock(type);
    setBlocks((prev) => [...prev, block]);
    setSelectedId(block.id);
  };
  const updateBlock = (updated) =>
    setBlocks((prev) => prev.map((b) => (b.id === updated.id ? updated : b)));
  const deleteBlock = (id) => {
    setBlocks((prev) => prev.filter((b) => b.id !== id));
    if (selectedId === id) setSelectedId(null);
  };
  const duplicateBlock = (id) => {
    const src = blocks.find((b) => b.id === id);
    if (!src) return;
    if (src.type === "image" && !canAddImage) return;
    if (src.type === "video" && !canAddVideo) return;
    const copy = { ...src, id: uid() };
    const idx = blocks.findIndex((b) => b.id === id);
    const next = [...blocks];
    next.splice(idx + 1, 0, copy);
    setBlocks(next);
    setSelectedId(copy.id);
  };
  const moveBlock = (id, dir) => {
    const idx = blocks.findIndex((b) => b.id === id);
    if (dir === "up" && idx === 0) return;
    if (dir === "down" && idx === blocks.length - 1) return;
    const next = [...blocks];
    const swap = dir === "up" ? idx - 1 : idx + 1;
    [next[idx], next[swap]] = [next[swap], next[idx]];
    setBlocks(next);
  };

  const handleDragStart = (e, id) => {
    dragItem.current = id;
    setDragId(id);
    e.dataTransfer.effectAllowed = "move";
  };
  const handleDragOver = (e, id) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (id !== dragItem.current) setDragOverId(id);
  };
  const handleDrop = (e, targetId) => {
    e.preventDefault();
    const fromId = dragItem.current;
    if (!fromId || fromId === targetId) {
      setDragOverId(null);
      setDragId(null);
      return;
    }
    const fromIdx = blocks.findIndex((b) => b.id === fromId);
    const toIdx = blocks.findIndex((b) => b.id === targetId);
    const next = [...blocks];
    const [moved] = next.splice(fromIdx, 1);
    next.splice(toIdx, 0, moved);
    setBlocks(next);
    setDragOverId(null);
    setDragId(null);
    dragItem.current = null;
  };
  const handleDragEnd = () => {
    setDragOverId(null);
    setDragId(null);
    dragItem.current = null;
  };
  const handleSidebarDragStart = (e, type) => {
    e.dataTransfer.setData("newBlockType", type);
    e.dataTransfer.effectAllowed = "copy";
  };
  const handleCanvasDrop = (e) => {
    const newType = e.dataTransfer.getData("newBlockType");
    if (newType) {
      e.preventDefault();
      if (newType === "image" && !canAddImage) return;
      if (newType === "video" && !canAddVideo) return;
      const block = createBlock(newType);
      setBlocks((prev) => [...prev, block]);
      setSelectedId(block.id);
    }
  };

  // ── FIXED: sanitize blocks → strip local srcs → generate clean HTML ───────
  const handleSave = async () => {
    setIsSaving(true);
    try {
      // 0. Convert local blob URLs to base64
      const processedBlocks = await Promise.all(
        blocks.map(async (block) => {
          if (
            (block.type === "image" || block.type === "video") &&
            block.src &&
            block.src.startsWith("blob:")
          ) {
            const base64 = await blobToBase64(block.src);
            return { ...block, src: base64 || block.src };
          }
          return block;
        }),
      );

      // 1. Strip any retaining local URLs from image and video blocks
      const sanitizedBlocks = sanitizeBlocksForSave(processedBlocks);

      // 2. Check how many local images were stripped (warn user)
      const strippedImages = processedBlocks.filter(
        (b) => (b.type === "image" || b.type === "video") && isLocalSrc(b.src),
      ).length;

      // 3. Generate HTML from sanitized blocks only
      const html = generateHTML(sanitizedBlocks, bgColor, emailBg);

      // 4. 100KB limit check
      const htmlSize = new Blob([html]).size;
      if (htmlSize > 100000) {
        toast.error(
          `Template too large: ${(htmlSize / 1024).toFixed(1)}KB. Please remove some elements to stay under 100KB.`,
        );
        return;
      }

      if (strippedImages > 0) {
        toast.warn(
          `${strippedImages} image/video block(s) with local URLs were excluded from the saved template. Only public URLs are saved.`,
          { autoClose: 5000 },
        );
      }

      // 5. Save sanitized data (no local URLs anywhere)
      const data = {
        name: templateName,
        blocks: sanitizedBlocks,
        bgColor,
        emailBg,
        html,
      };
      setSaveData(data);
      setSaveDialogOpen(true);
      setEmailSent(false);
      setEmailError("");
      setSavedOk(true);
      setTimeout(() => setSavedOk(false), 2500);

      // Revert state back with sanitized blocks (so the canvas updates with base64)
      setBlocks(sanitizedBlocks);
    } finally {
      setIsSaving(false);
    }
  };

  // ── Confirm Save → send email with stored HTML ────────────────────────────
  const handleConfirmSave = async () => {
    if (!saveData?.html) return;
    setSendingEmail(true);
    setEmailError("");
    setEmailSent(false);

    try {
      const res = await fetch(SEND_EMAIL_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          html: saveData.html,
          templateName: saveData.name,
        }),
      });

      const result = await res.json();
      if (!res.ok) throw new Error(result.error || "Email send failed");

      console.log("✅ Email sent, messageId:", result.messageId);
      setEmailSent(true);
    } catch (err) {
      console.error("❌ Send failed:", err.message);
      setEmailError(err.message);
    } finally {
      setSendingEmail(false);
    }
  };

  const handleCloseDialog = () => {
    setSaveDialogOpen(false);
    setEmailSent(false);
    setEmailError("");
  };

  const selectedBlock = blocks.find((b) => b.id === selectedId) || null;

  return (
    <Box
      sx={{
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        background: "transparent",
        overflow: "hidden",
      }}
    >
      {/* TOP BAR */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          px: 2,
          py: 1,
          backgroundColor: "rgba(26,32,62,0.7)",
          backdropFilter: "blur(20px)",
          WebkitBackdropFilter: "blur(20px)",
          borderBottom: "1px solid rgba(255,255,255,0.1)",
          flexShrink: 0,
        }}
      >
        <IconButton
          size="small"
          sx={{
            color: "#fff",
            border: "1px solid rgba(255,255,255,0.2)",
            borderRadius: "6px",
            p: 0.5,
          }}
          onClick={() => window.history.back()}
        >
          <ArrowBackIcon sx={{ fontSize: 18 }} />
        </IconButton>
        <Divider
          orientation="vertical"
          flexItem
          sx={{ borderColor: "rgba(255,255,255,0.15)" }}
        />
        <TextField
          value={templateName}
          onChange={(e) => setTemplateName(e.target.value)}
          variant="standard"
          size="small"
          sx={{
            "& input": { color: "#fff", fontWeight: 600, fontSize: "0.9rem" },
            "& .MuiInput-underline:before": {
              borderBottomColor: "rgba(255,255,255,0.3)",
            },
            "& .MuiInput-underline:after": { borderBottomColor: "#4fc3f7" },
          }}
        />
        <Box sx={{ display: "flex", gap: 1, mb: 1, width:"20%" }} >
        <FormControl sx={{ flex: 1 }}>
          <InputLabel id="Category-label" sx={{ fontSize: "0.8rem" }}>
            Template Category
          </InputLabel>
          <Select
            labelId="Category-label"
                id="Category-label"
                label="Template Category"
                value={template_Category}
                onChange={(e) => setTemplateCategory(e.target.value)}
                size="medium"
                sx={{
                  height: 35,
                  fontSize: "0.8rem",
                  background: "rgba(255,255,255,0.7)",
                  backdropFilter: "blur(8px)",
                  borderRadius: "8px",
                  "&:hover": {
                    boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
                  },
                }}
              >
                <MenuItem value="promotional">Promotional</MenuItem>
                <MenuItem value="transactional">Transactional</MenuItem>
            </Select>
        </FormControl>
        </Box>
        <Box sx={{ ml: "auto", display: "flex", gap: 1, alignItems: "center" }}>
          {savedOk && (
            <Chip
              label="Saved ✓"
              size="small"
              sx={{
                backgroundColor: "#1b5e20",
                color: "#a5d6a7",
                fontSize: "0.72rem",
                height: 24,
              }}
            />
          )}
          <Button
            size="small"
            startIcon={<VisibilityIcon sx={{ fontSize: 16 }} />}
            onClick={() => setPreviewOpen(true)}
            sx={{
              color: "#90caf9",
              borderColor: "rgba(144,202,249,0.4)",
              border: "1px solid",
              textTransform: "none",
              fontSize: "0.78rem",
              py: 0.5,
              px: 1.5,
            }}
          >
            Preview
          </Button>
          <Button
            size="small"
            variant="contained"
            startIcon={<SaveOutlinedIcon sx={{ fontSize: 16 }} />}
            onClick={handleSave}
            sx={{
              backgroundColor: "#1976d2",
              textTransform: "none",
              fontSize: "0.78rem",
              py: 0.5,
              px: 1.75,
              "&:hover": { backgroundColor: "#1565c0" },
            }}
          >
            Save Template
          </Button>
        </Box>
      </Box>

      {/* MAIN */}
      <Box sx={{ flex: 1, display: "flex", overflow: "hidden" }}>
        {/* LEFT SIDEBAR */}
        <Box
          sx={{
            width: 200,
            flexShrink: 0,
            backgroundColor: "rgba(255,255,255,0.04)",
            backdropFilter: "blur(20px)",
            borderRight: "1px solid rgba(255,255,255,0.08)",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              px: 1.5,
              py: 1.25,
              borderBottom: "1px solid rgba(255,255,255,0.08)",
            }}
          >
            <Typography
              sx={{
                fontSize: "0.68rem",
                fontWeight: 700,
                color: "rgba(255,255,255,0.45)",
                letterSpacing: 1,
                textTransform: "uppercase",
              }}
            >
              Elements
            </Typography>
            <Typography
              sx={{
                fontSize: "0.65rem",
                color: "rgba(255, 255, 255, 0.5)",
                mt: 0.25,
              }}
            >
              Drag onto canvas or click to add
            </Typography>
          </Box>
          <Box sx={{ flex: 1, overflowY: "auto", p: 1 }}>
            {BLOCK_TYPES.map((bt) => {
              const disabled =
                (bt.type === "image" && !canAddImage) ||
                (bt.type === "video" && !canAddVideo);
              return (
                <Tooltip
                  key={bt.type}
                  title={
                    bt.type === "image"
                      ? `Images: ${totalImages}/${MAX_IMAGES}`
                      : bt.type === "video"
                        ? `Videos: ${totalVideos}/${MAX_VIDEOS}`
                        : ""
                  }
                  placement="right"
                >
                  <Box
                    draggable={!disabled}
                    onDragStart={(e) => handleSidebarDragStart(e, bt.type)}
                    onClick={() => !disabled && addBlock(bt.type)}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      px: 1.25,
                      py: 0.9,
                      borderRadius: "6px",
                      cursor: disabled ? "not-allowed" : "grab",
                      opacity: disabled ? 0.4 : 1,
                      mb: 0.25,
                      border: "1px solid transparent",
                      transition: "all 0.15s",
                      "&:hover": disabled
                        ? {}
                        : {
                            backgroundColor: "rgba(99,102,241,0.12)",
                            border: "1px solid rgba(99,102,241,0.3)",
                          },
                      "&:active": { cursor: "grabbing" },
                    }}
                  >
                    <Box
                      sx={{
                        color: bt.color,
                        display: "flex",
                        "& svg": { fontSize: 18 },
                      }}
                    >
                      {bt.icon}
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography
                        sx={{
                          fontSize: "0.75rem",
                          fontWeight: 600,
                          color: "#e2e8f0",
                        }}
                      >
                        {bt.label}
                      </Typography>
                      {(bt.type === "image" || bt.type === "video") && (
                        <Typography
                          sx={{
                            fontSize: "0.62rem",
                            color: disabled
                              ? "#f87171"
                              : "rgba(255,255,255,0.35)",
                          }}
                        >
                          {bt.type === "image"
                            ? `${totalImages}/${MAX_IMAGES}`
                            : `${totalVideos}/${MAX_VIDEOS}`}
                        </Typography>
                      )}
                      {bt.type === "button_row" && (
                        <Typography
                          sx={{
                            fontSize: "0.62rem",
                            color: "rgba(255,255,255,0.35)",
                          }}
                        >
                          Side by side
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Tooltip>
              );
            })}
            <Divider sx={{ my: 1.5, borderColor: "rgba(255,255,255,0.1)" }} />
            <Typography
              sx={{
                fontSize: "0.68rem",
                fontWeight: 700,
                color: "rgba(255,255,255,0.45)",
                letterSpacing: 1,
                textTransform: "uppercase",
                mb: 1,
              }}
            >
              Canvas
            </Typography>
            <Box sx={{ mb: 1 }}>
              <Typography
                sx={{
                  fontSize: "0.7rem",
                  color: "rgba(255,255,255,0.5)",
                  mb: 0.5,
                }}
              >
                Email Background
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                <input
                  type="color"
                  value={emailBg}
                  onChange={(e) => setEmailBg(e.target.value)}
                  style={{
                    width: 28,
                    height: 24,
                    border: "1px solid rgba(255,255,255,0.15)",
                    borderRadius: 3,
                    cursor: "pointer",
                    padding: 1,
                    background: "transparent",
                  }}
                />
                <Typography
                  sx={{ fontSize: "0.68rem", color: "rgba(255,255,255,0.35)" }}
                >
                  {emailBg}
                </Typography>
              </Box>
            </Box>
            <Box>
              <Typography
                sx={{
                  fontSize: "0.7rem",
                  color: "rgba(255,255,255,0.5)",
                  mb: 0.5,
                }}
              >
                Content Background
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                <input
                  type="color"
                  value={bgColor}
                  onChange={(e) => setBgColor(e.target.value)}
                  style={{
                    width: 28,
                    height: 24,
                    border: "1px solid rgba(255,255,255,0.15)",
                    borderRadius: 3,
                    cursor: "pointer",
                    padding: 1,
                    background: "transparent",
                  }}
                />
                <Typography
                  sx={{ fontSize: "0.68rem", color: "rgba(255,255,255,0.35)" }}
                >
                  {bgColor}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>

        {/* CANVAS */}
        <Box
          sx={{ flex: 1, overflowY: "auto", backgroundColor: emailBg, p: 3 }}
          onDragOver={(e) => e.preventDefault()}
          onDrop={handleCanvasDrop}
        >
          <Box sx={{ maxWidth: 620, mx: "auto" }}>
            <Box
              sx={{
                backgroundColor: bgColor,
                borderRadius: "8px",
                boxShadow: "0 2px 16px rgba(0,0,0,0.10)",
                overflow: "hidden",
                minHeight: 400,
              }}
            >
              <Box sx={{ backgroundColor: "#1a203e", px: 3, py: 1.5 }}>
              </Box>
              <Box sx={{ p: 3 }}>
                {blocks.length === 0 ? (
                  <Box
                    sx={{
                      minHeight: 200,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      border: "2px dashed #c5cae9",
                      borderRadius: "8px",
                      backgroundColor: "#f8f9ff",
                      gap: 1,
                    }}
                  >
                    <AddIcon sx={{ fontSize: 36, color: "#c5cae9" }} />
                    <Typography sx={{ fontSize: "0.78rem", color: "#b0bec5" }}>
                      Drag elements here or click to add
                    </Typography>
                  </Box>
                ) : (
                  blocks.map((block, idx) => {
                    const isSelected = selectedId === block.id;
                    const isDraggingThis = dragId === block.id;
                    const isDropTarget = dragOverId === block.id;
                    return (
                      <Box
                        key={block.id}
                        draggable
                        onDragStart={(e) => handleDragStart(e, block.id)}
                        onDragOver={(e) => handleDragOver(e, block.id)}
                        onDrop={(e) => handleDrop(e, block.id)}
                        onDragEnd={handleDragEnd}
                        onClick={() =>
                          setSelectedId(isSelected ? null : block.id)
                        }
                        sx={{
                          position: "relative",
                          mb: 1,
                          borderRadius: "6px",
                          border: isSelected
                            ? "2px solid #1976d2"
                            : isDropTarget
                              ? "2px dashed #1976d2"
                              : "2px solid transparent",
                          opacity: isDraggingThis ? 0.4 : 1,
                          backgroundColor: isDropTarget
                            ? "#e3f2fd"
                            : "transparent",
                          transition: "border 0.15s, opacity 0.15s",
                          cursor: "pointer",
                          "&:hover": {
                            border: isSelected
                              ? "2px solid #1976d2"
                              : "2px solid #90caf9",
                          },
                          "&:hover .block-toolbar": { opacity: 1 },
                        }}
                      >
                        <Box
                          className="block-toolbar"
                          sx={{
                            position: "absolute",
                            top: -16,
                            right: 4,
                            display: "flex",
                            gap: 0.25,
                            opacity: isSelected ? 1 : 0,
                            transition: "opacity 0.15s",
                            backgroundColor: "#1a203e",
                            borderRadius: "6px",
                            px: 0.5,
                            py: 0.25,
                            zIndex: 10,
                          }}
                        >
                          <Tooltip title="Move up">
                            <span>
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  moveBlock(block.id, "up");
                                }}
                                disabled={idx === 0}
                                sx={{
                                  p: 0.25,
                                  color:
                                    idx === 0
                                      ? "rgba(255,255,255,0.2)"
                                      : "#fff",
                                }}
                              >
                                <ArrowUpwardIcon sx={{ fontSize: 13 }} />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title="Move down">
                            <span>
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  moveBlock(block.id, "down");
                                }}
                                disabled={idx === blocks.length - 1}
                                sx={{
                                  p: 0.25,
                                  color:
                                    idx === blocks.length - 1
                                      ? "rgba(255,255,255,0.2)"
                                      : "#fff",
                                }}
                              >
                                <ArrowDownwardIcon sx={{ fontSize: 13 }} />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title="Duplicate">
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                duplicateBlock(block.id);
                              }}
                              sx={{ p: 0.25, color: "#fff" }}
                            >
                              <ContentCopyIcon sx={{ fontSize: 13 }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                deleteBlock(block.id);
                              }}
                              sx={{ p: 0.25, color: "#ef9a9a" }}
                            >
                              <DeleteOutlineIcon sx={{ fontSize: 13 }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Drag to reorder">
                            <IconButton
                              size="small"
                              sx={{
                                p: 0.25,
                                color: "rgba(255,255,255,0.5)",
                                cursor: "grab",
                              }}
                            >
                              <DragIndicatorIcon sx={{ fontSize: 13 }} />
                            </IconButton>
                          </Tooltip>
                        </Box>

                        {isSelected && (
                          <Box
                            sx={{
                              position: "absolute",
                              top: -16,
                              left: 4,
                              zIndex: 10,
                            }}
                          >
                            <Chip
                              label={
                                BLOCK_TYPES.find((b) => b.type === block.type)
                                  ?.label || block.type
                              }
                              size="small"
                              sx={{
                                height: 16,
                                fontSize: "0.6rem",
                                backgroundColor: "#1976d2",
                                color: "#fff",
                                "& .MuiChip-label": { px: 0.75 },
                              }}
                            />
                          </Box>
                        )}
                        <Box sx={{ p: 1.5 }}>
                          <BlockRenderer block={block} />
                        </Box>
                      </Box>
                    );
                  })
                )}
              </Box>
              <Box
                sx={{
                  backgroundColor: "#f8f8f8",
                  borderTop: "1px solid #ececec",
                  px: 3,
                  py: 2,
                  textAlign: "center",
                }}
              >
                <Typography sx={{ fontSize: "0.68rem", color: "#bbb" }}>
                  © 2026 ·{" "}
                  <span
                    style={{
                      color: "#90a4ae",
                      textDecoration: "underline",
                      cursor: "pointer",
                    }}
                  >
                    Unsubscribe
                  </span>{" "}
                  ·{" "}
                  <span
                    style={{
                      color: "#90a4ae",
                      textDecoration: "underline",
                      cursor: "pointer",
                    }}
                  >
                    View in browser
                  </span>
                </Typography>
              </Box>
            </Box>
            <Typography
              sx={{
                textAlign: "center",
                fontSize: "0.68rem",
                color: "rgba(255,255,255,0.35)",
                mt: 1.5,
              }}
            >
              {blocks.length} block{blocks.length !== 1 ? "s" : ""} · Images:{" "}
              {totalImages}/{MAX_IMAGES} · Videos: {totalVideos}/{MAX_VIDEOS}
            </Typography>
          </Box>
        </Box>

        {/* RIGHT SIDEBAR */}
        <Box
          sx={{
            width: 240,
            flexShrink: 0,
            backgroundColor: "rgba(255,255,255,0.04)",
            backdropFilter: "blur(20px)",
            borderLeft: "1px solid rgba(255,255,255,0.08)",
            overflowY: "auto",
          }}
        >
          <Box
            sx={{
              px: 1.5,
              py: 1.25,
              borderBottom: "1px solid rgba(255,255,255,0.08)",
            }}
          >
            <Typography
              sx={{
                fontSize: "0.68rem",
                fontWeight: 700,
                color: "rgba(255,255,255,0.45)",
                letterSpacing: 1,
                textTransform: "uppercase",
              }}
            >
              {selectedBlock
                ? `Edit · ${BLOCK_TYPES.find((b) => b.type === selectedBlock.type)?.label}`
                : "Properties"}
            </Typography>
          </Box>
          <PropertiesPanel
            block={selectedBlock}
            onChange={updateBlock}
            imageCount={imageCount}
            videoCount={videoCount}
          />
        </Box>
      </Box>

      {/* PREVIEW DIALOG */}
      <Dialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: "12px", overflow: "hidden" } }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            py: 1.5,
            backgroundColor: "#1a203e",
            color: "#fff",
          }}
        >
          <Typography sx={{ fontWeight: 600, fontSize: "0.88rem" }}>
            Preview — {templateName}
          </Typography>
          <IconButton
            size="small"
            onClick={() => setPreviewOpen(false)}
            sx={{ color: "#fff" }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0, backgroundColor: emailBg }}>
          <Box sx={{ p: 2 }}>
            <Box
              sx={{
                backgroundColor: bgColor,
                borderRadius: "8px",
                boxShadow: "0 2px 16px rgba(0,0,0,0.1)",
                overflow: "hidden",
              }}
            >
              <Box sx={{ backgroundColor: "#1a203e", px: 3, py: 1.5 }}>
                <Typography
                  sx={{
                    fontSize: "0.7rem",
                    color: "rgba(255,255,255,0.5)",
                    textAlign: "center",
                  }}
                >
                  Your Company Name
                </Typography>
              </Box>
              <Box sx={{ p: 3 }}>
                {blocks.map((block) => (
                  <Box key={block.id} sx={{ mb: 1.5 }}>
                    <BlockRenderer block={block} />
                  </Box>
                ))}
              </Box>
              <Box
                sx={{
                  backgroundColor: "#f8f8f8",
                  borderTop: "1px solid #ececec",
                  px: 3,
                  py: 2,
                  textAlign: "center",
                }}
              >
                <Typography sx={{ fontSize: "0.68rem", color: "#bbb" }}>
                  © 2026 · Unsubscribe · View in browser
                </Typography>
              </Box>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>

      {/* SAVE DIALOG */}
      <Dialog
        open={saveDialogOpen}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: "12px" } }}
      >
        <DialogTitle
          sx={{
            backgroundColor: "#1a203e",
            color: "#fff",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography sx={{ fontWeight: 600 }}>Save Template</Typography>
          <IconButton
            size="small"
            onClick={handleCloseDialog}
            sx={{ color: "#fff" }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 3 }}>
          {/* Info banner: local images stripped */}
          <Box
            sx={{
              mb: 2.5,
              p: 2,
              backgroundColor: "#e8f5e9",
              border: "1px solid #a5d6a7",
              borderRadius: "8px",
            }}
          >
            <Typography
              sx={{
                fontSize: "0.8rem",
                fontWeight: 700,
                color: "#1b5e20",
                mb: 0.5,
              }}
            >
              ✅ Local URLs Excluded
            </Typography>
            <Typography sx={{ fontSize: "0.75rem", color: "#2e7d32" }}>
              Any images or videos uploaded from your device (blob: / localhost
              URLs) have been automatically excluded from the saved HTML. Only
              public URLs are included.
            </Typography>
          </Box>

          {/* Send status feedback */}
          {emailSent && (
            <Box
              sx={{
                mb: 2,
                p: 1.5,
                backgroundColor: "#e8f5e9",
                border: "1px solid #a5d6a7",
                borderRadius: "8px",
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <CheckCircleOutlineIcon sx={{ color: "#2e7d32", fontSize: 20 }} />
              <Typography
                sx={{ fontSize: "0.8rem", color: "#2e7d32", fontWeight: 600 }}
              >
                Email sent successfully!
              </Typography>
            </Box>
          )}
          {emailError && (
            <Box
              sx={{
                mb: 2,
                p: 1.5,
                backgroundColor: "#fce4ec",
                border: "1px solid #f48fb1",
                borderRadius: "8px",
              }}
            >
              <Typography
                sx={{ fontSize: "0.8rem", color: "#c62828", fontWeight: 600 }}
              >
                ❌ Failed to send email
              </Typography>
              <Typography
                sx={{ fontSize: "0.73rem", color: "#c62828", mt: 0.5 }}
              >
                {emailError}
              </Typography>
              <Typography sx={{ fontSize: "0.68rem", color: "#999", mt: 0.5 }}>
                Is the backend running on port 4000?
              </Typography>
            </Box>
          )}

          <Typography
            sx={{
              fontSize: "0.75rem",
              fontWeight: 700,
              mb: 1,
              color: "#1a203e",
            }}
          >
            JSON DATA
          </Typography>
          <Box
            sx={{
              backgroundColor: "#f5f5f5",
              p: 2,
              borderRadius: "8px",
              border: "1px solid #e0e0e0",
              overflowX: "auto",
              maxHeight: "22vh",
              mb: 3,
            }}
          >
            <pre
              style={{
                margin: 0,
                fontSize: "0.75rem",
                fontFamily: "monospace",
              }}
            >
              {JSON.stringify(
                saveData
                  ? { ...saveData, html: "(HTML string — see below)" }
                  : null,
                null,
                2,
              )}
            </pre>
          </Box>

          <Typography
            sx={{
              fontSize: "0.75rem",
              fontWeight: 700,
              mb: 1,
              color: "#1a203e",
            }}
          >
            GENERATED HTML (email body)
          </Typography>
          <Box
            sx={{
              backgroundColor: "#f5f5f5",
              p: 2,
              borderRadius: "8px",
              border: "1px solid #e0e0e0",
              overflowX: "auto",
              maxHeight: "22vh",
            }}
          >
            <pre
              style={{
                margin: 0,
                fontSize: "0.75rem",
                fontFamily: "monospace",
                whiteSpace: "pre-wrap",
              }}
            >
              {saveData?.html}
            </pre>
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 2, borderTop: "1px solid #f0f0f0", gap: 1 }}>
          <Button
            onClick={handleCloseDialog}
            sx={{ textTransform: "none", color: "#666" }}
          >
            Close
          </Button>
          <Button
            variant="contained"
            startIcon={
              isSaving ? (
                <CircularProgress size={14} color="inherit" />
              ) : (
                <SaveOutlinedIcon sx={{ fontSize: 16 }} />
              )
            }
            onClick={handlesubmit}
            disabled={isSaving}
            sx={{
              backgroundColor: "#1976d2",
              textTransform: "none",
              "&:hover": { backgroundColor: "#1565c0" },
            }}
          >
            {isSaving ? "Saving..." : "Save Template"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EmailTemplateBuilder;
